import React, { useState, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";

import { addCustomer } from "../../../services/clearing/customers";
import { Customer } from "../../../types/customer";
import CustomerForm from "./forms/CustomerForm";
import { validateCustomerForm } from "../utils/customerValidation";

const emptyCustomer: Customer = {
  account_holder_name: "",
  iban: "",
  station_id: "",
  bic: "",
  email: "",
  recipient_emails: [],
  phone: "",
  detailed_invoice: false,
  attach_csv_to_credit_note: false,
  address: {
    company_name: "",
    recipient_name: "",
    street_address: "",
    country: "DE",
    postal_code: "",
    city: "",
  },
  invoice_language: "DE",
  vat_id: null,
  business_type: "PRIVATE",
};

interface AddCustomerDialogProps {
  visible: boolean;
  onClose: () => void;
  onCustomerCreated: (customer: any) => void;
}

export const AddCustomerDialog: React.FC<AddCustomerDialogProps> = ({
  visible,
  onClose,
  onCustomerCreated,
}) => {
  const [customer, setCustomer] = useState<Customer>({ ...emptyCustomer });
  const toast = useRef(null);

  const handleChange = (field: string, value: any, nested = false) => {
    if (nested) {
      setCustomer((prev) => {
        const newCustomer = { ...prev };
        const keys = field.split(".");
        let current: any = newCustomer;

        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          if (!current[key] || typeof current[key] !== "object") {
            current[key] = {};
          }
          current = current[key];
        }

        current[keys[keys.length - 1]] = value;
        return newCustomer;
      });
    } else {
      setCustomer((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSubmit = async () => {
    const missingFields = validateCustomerForm(customer);

    if (missingFields.length > 0) {
      toast?.current?.show({
        severity: "error",
        summary: "Missing Required Fields",
        detail: `Please fill in: ${missingFields.join(", ")}`,
        life: 5000,
      });
      return;
    }

    try {
      const created = await addCustomer(customer);
      toast?.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Customer added",
        life: 3000,
      });
      setCustomer({ ...emptyCustomer });
      onCustomerCreated(created);
    } catch {
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to add customer",
        life: 3000,
      });
    }
  };

  const handleClose = () => {
    setCustomer({ ...emptyCustomer });
    onClose();
  };

  return (
    <Dialog
      header="Add New Customer"
      visible={visible}
      style={{ width: "60vw" }}
      onHide={handleClose}
      maximizable
    >
      <Toast ref={toast} />
      <CustomerForm
        submitLabel="Save"
        title="Customer Details"
        customer={customer}
        onFieldChange={handleChange}
        onSubmit={handleSubmit}
        onCancel={handleClose}
      />
    </Dialog>
  );
};
